/**
 * Template ÚNICO de exibição de empresa com o código (codi_emp) — pedido 08/09:
 * "o codi_emp já trafega em tudo — é a chave; mostre o código junto do nome".
 *
 * Toda formatação nome+código da página sai daqui (e do componente `NomeEmpresa`,
 * que consome estas funções) — nunca de concatenação inline duplicada:
 * - "sufixo": código à direita colado ao nome com underscore ("NomeEmpresa_1117") —
 *   card Fila (3 níveis) e labels dos selects de empresa (localizável ao digitar);
 * - "bloco": código ABAIXO do nome (variante visual do componente — mesma regra de
 *   fallback daqui) — popups dos nós e coluna Empresa do Resumo.
 *
 * Fallbacks (contrato): sem nome → só código; sem código → só nome. Como
 * `nomeEmpresa()` já cai em codi_emp quando o cadastro não tem nome, nome IGUAL ao
 * código é tratado como "sem nome" (senão a tela mostraria "1117_1117").
 */

/** Há nome E código DISTINTOS para exibir em duas partes? */
export const temCodigoDistinto = (
  nome: string | null | undefined,
  codigo: string | null | undefined,
): boolean => {
  const n = (nome ?? '').trim();
  const c = (codigo ?? '').trim();
  return Boolean(n && c && n !== c);
};

/**
 * Rótulo textual completo — "NomeEmpresa_1117" (ou só a parte disponível). É o que
 * vai para tooltips/`title` e para os labels dos selects (a busca nativa do select
 * encontra tanto pelo nome quanto pelo código).
 */
export const rotuloComCodigo = (
  nome: string | null | undefined,
  codigo: string | null | undefined,
): string => {
  const n = (nome ?? '').trim();
  const c = (codigo ?? '').trim();
  if (!n) return c;
  if (!c || n === c) return n;
  return `${n}_${c}`;
};

/**
 * Modo do seletor de TIPO de pesquisa da aba Resumo (pedido 08/09, reforma dos
 * filtros): "razao" = razão social (o nome exibido da empresa, `nomeEmpresa()`);
 * "id" = código da empresa (codi_emp).
 */
export type ModoBusca = 'razao' | 'id';

/**
 * Transicao do seletor de TIPO de pesquisa (achado da revisao 08/09): trocar o modo
 * LIMPA o termo digitado — o termo de um campo quase nunca casa no outro, e mante-lo
 * zerava o resultado em silencio. Mesma familia dos `ao*` de filtrosCompartilhados
 * (transicao pura, testavel fora do componente). Valor desconhecido cai no default
 * "razao", como o `(novo || 'razao')` que o select ja' fazia.
 */
export const aoTrocarModoBusca = (novoModo: string): { modo: ModoBusca; busca: string } => ({
  modo: novoModo === 'id' ? 'id' : 'razao',
  busca: '',
});

/**
 * A busca textual do Resumo casa SÓ no campo escolhido pelo seletor de tipo de
 * pesquisa (contains, case-insensitive) — substitui o nome-OU-código automático da
 * Fase 12 na aba Resumo. `termo` deve chegar já normalizado (trim + lowercase),
 * como o pipeline de filtros existente faz uma vez só por chamada.
 */
export const casaBuscaPorModo = (
  nome: string,
  codigo: string,
  termoNormalizado: string,
  modo: ModoBusca,
): boolean =>
  modo === 'id'
    ? codigo.toLowerCase().includes(termoNormalizado)
    : nome.toLowerCase().includes(termoNormalizado);
